import { Marked } from 'marked';
import type {
  MarkedExtension,
  RendererObject,
  TokenizerExtension,
  TokenizerObject,
  Tokens,
} from 'marked';
import { simpleXssValidator as SimpleXssValidator } from './simpleXssValidator';
import { urlUtils } from '@ardoq/common-helpers';
import { escapeHTML } from '@ardoq/html';
import { CELL_NL_SENTINEL, CELL_NL_SENTINEL_RE } from './cellSentinel';
import type { MarkdownFlavor } from '../markdownFlavor';

const COLOR_OPEN_RE = /\{color: ?([^/;}]+)?\/([^/;}]+)?\}/gm;
const COLOR_CLOSE_RE = /\{color\}/gm;
const EMBED_RE = /\[(youtube|vimeo):(.+?)\]/gm;
const ESCAPED_STRIKETHROUGH_RE = /\\~\\~(.*?)\\~\\~/gm;
const ESCAPED_PIPE_RE = /\\\|/gm;
const VIDEO_ID_RE = /^[\w-]+$/;
const CODE_BLOCK_RE = /(<pre[\s\S]*?<\/pre>|<code[\s\S]*?<\/code>)/;

const renderColorSpan = (
  _match: string,
  fgColor: string | undefined,
  bgColor: string | undefined
): string => {
  const style = [
    fgColor && SimpleXssValidator.validateColor(fgColor)
      ? `color: ${fgColor}`
      : '',
    bgColor && SimpleXssValidator.validateColor(bgColor)
      ? `background-color: ${bgColor}`
      : '',
  ]
    .filter(Boolean)
    .join('; ');
  return `<span class="color" style="${style}">`;
};

const renderEmbed = (
  match: string,
  source: string,
  videoId: string
): string => {
  if (!VIDEO_ID_RE.test(videoId)) return match;
  const src =
    source.toLowerCase() === 'youtube'
      ? `https://www.youtube.com/embed/${videoId}`
      : `https://player.vimeo.com/video/${videoId}`;
  const attributes = [
    `src="${src}"`,
    'width="500"',
    'height="256"',
    'frameborder="0"',
    'webkitallowfullscreen',
    'mozallowfullscreen',
    'allowfullscreen',
  ].join(' ');
  return `<iframe ${attributes}></iframe>`;
};

const applyPostProcessReplacements = (html: string): string =>
  html
    .replace(COLOR_OPEN_RE, renderColorSpan)
    .replace(COLOR_CLOSE_RE, '</span>')
    .replace(EMBED_RE, renderEmbed)
    .replace(ESCAPED_STRIKETHROUGH_RE, '~~$1~~')
    .replace(ESCAPED_PIPE_RE, '|');

// Replacements must skip code, where `[youtube:…]` and `{color:…}` are literal.
const postProcessHtml = (html: string): string =>
  html
    .split(CODE_BLOCK_RE)
    .map((segment, index) =>
      index % 2 === 0 ? applyPostProcessReplacements(segment) : segment
    )
    .join('');

const escapeHTMLForCode = (str: string): string =>
  str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

const buildLinkOpenHtml = (href: string, title: string | null): string => {
  const sanitizedHref = urlUtils.sanitizeUrl(href);
  const attributes: string[] = [];

  if (sanitizedHref.startsWith('comp://')) {
    const ids = sanitizedHref.slice('comp://'.length).split('/');
    if (ids.length === 2) {
      const [wsId, compId] = ids;
      const escapedCompId = escapeHTML(compId);
      attributes.push(
        `href="${
          wsId
            ? `/app/view/reader/workspace/${escapeHTML(wsId)}/component/${escapedCompId}`
            : escapedCompId
        }"`
      );
      attributes.push(`id="${escapedCompId}"`);
      attributes.push('class="inlineComp"');
    }
  } else {
    if (
      sanitizedHref.startsWith('/') ||
      sanitizedHref.startsWith('http://') ||
      sanitizedHref.startsWith('https://') ||
      sanitizedHref.startsWith('mailto:')
    ) {
      attributes.push(`href="${sanitizedHref}"`);
    } else {
      attributes.push(`href="https://${sanitizedHref}"`);
    }
    attributes.push('rel="nofollow noreferrer"');
    if (!sanitizedHref.startsWith('mailto:')) {
      attributes.push('target="_blank"');
    }
  }

  const titleAttr = escapeHTML(title ?? null);
  if (titleAttr) attributes.push(`title="${titleAttr}"`);

  return `<a ${attributes.join(' ')}>`;
};

// marked's `cleanUrl` (encodeURI with `%25` restored). Replicated so our image
// renderer produces the same percent-encoded `src` as marked's default — the
// behaviour the ARD-4972/ARD-4953 XSS tests pin down.
const cleanUrl = (href: string): string | null => {
  try {
    return encodeURI(href).replace(/%25/g, '%');
  } catch {
    return null;
  }
};

const SIZE_IMAGE_RE =
  /^!\[([^\]]*)\]\(\s*(\S+?)(?:\s+(?:"([^"]*)"|'([^']*)'))?\s+=(?:(\d+)x(\d+)|(small|medium|large))\s*\)/;

type ImageWithSize = Tokens.Image & {
  width?: string;
  height?: string;
  size?: string;
};

const imageWithDimensions: TokenizerExtension = {
  name: 'imageWithDimensions',
  level: 'inline',
  start(src) {
    return src.indexOf('![');
  },
  tokenizer(src) {
    const match = SIZE_IMAGE_RE.exec(src);
    if (!match) return undefined;
    const [raw, alt, href, doubleTitle, singleTitle, width, height, size] =
      match;
    const title = doubleTitle ?? singleTitle ?? null;
    const token: ImageWithSize = {
      type: 'image',
      raw,
      href,
      title,
      text: alt,
      tokens: this.lexer.inlineTokens(alt),
      width,
      height,
      size,
    };
    return token;
  },
};

const renderImage: RendererObject['image'] = function (token) {
  const { href, title, text, tokens } = token;
  const { width, height, size } = token as ImageWithSize;
  const altText = tokens?.length
    ? this.parser.parseInline(tokens, this.parser.textRenderer)
    : text;
  const cleaned = cleanUrl(href);
  if (cleaned === null) return altText;

  let html = `<img src="${cleaned}" alt="${escapeHTML(altText)}"`;
  if (width && height) html += ` width="${width}" height="${height}"`;
  if (size) html += ` data-size="${size}"`;
  if (title) html += ` title="${escapeHTML(title)}"`;
  return `${html}>`;
};

const renderLink: RendererObject['link'] = function (token) {
  const open = buildLinkOpenHtml(token.href, token.title ?? null);
  return `${open}${this.parser.parseInline(token.tokens)}</a>`;
};

const renderCode: RendererObject['code'] = function (token) {
  const info = token.lang ? token.lang.trim() : '';
  const language = info.split(/\s+/)[0] ?? '';
  const escapedContent = escapeHTMLForCode(token.text);
  if (!language) {
    return `<pre><code>${escapedContent}\n</code></pre>`;
  }
  const escapedLanguage = escapeHTML(language);
  return `<pre><code data-language="${escapedLanguage}" class="prettyprint lang-${escapedLanguage}">${escapedContent}\n</code></pre>`;
};

const renderHtml: RendererObject['html'] = ({ text }) =>
  escapeHTMLForCode(text);

const renderListItem: RendererObject['listitem'] = function (item) {
  let body = '';
  for (const token of item.tokens) {
    if (BLOCK_TOKEN_TYPES.has(token.type) && body && !body.endsWith('\n')) {
      body += '\n';
    }
    body += this.parser.parse([token], Boolean(item.loose));
  }
  return `<li>${body}</li>\n`;
};

const BLOCK_TOKEN_TYPES = new Set([
  'list',
  'blockquote',
  'code',
  'table',
  'html',
  'hr',
  'paragraph',
  'space',
]);

const SINGLE_PARAGRAPH_RE = /^<p>((?:(?!<\/p>)[\s\S])*)<\/p>\n$/;

const buildRenderer = (reParseCell: (markdown: string) => string) => {
  const renderTableCell: RendererObject['tablecell'] = function (token) {
    const tag = token.header ? 'th' : 'td';
    const alignAttr =
      token.align === 'center' || token.align === 'right'
        ? ` align="${token.align}"`
        : '';
    const open = `<${tag}${alignAttr}>`;

    if (token.text.includes(CELL_NL_SENTINEL)) {
      const cellMarkdown = token.text.replace(CELL_NL_SENTINEL_RE, '\n');
      const blockHtml = reParseCell(cellMarkdown);
      const singleParagraph = SINGLE_PARAGRAPH_RE.exec(blockHtml);
      const content = singleParagraph ? singleParagraph[1] : blockHtml;
      return `${open}${content}</${tag}>\n`;
    }

    return `${open}${this.parser.parseInline(token.tokens)}</${tag}>\n`;
  };

  const renderTableRow: RendererObject['tablerow'] = ({ text }) =>
    `<tr>\n${text}</tr>\n`;

  const renderTable: RendererObject['table'] = function (token) {
    let headerCells = '';
    for (const cell of token.header) headerCells += this.tablecell(cell);
    const header = this.tablerow({ text: headerCells });

    let bodyRows = '';
    for (const row of token.rows) {
      let cells = '';
      for (const cell of row) cells += this.tablecell(cell);
      bodyRows += this.tablerow({ text: cells });
    }
    const body = bodyRows ? `<tbody>\n${bodyRows}</tbody>\n` : '';

    return `<table>\n<thead>\n${header}</thead>\n${body}</table>\n`;
  };

  const renderer: RendererObject = {
    code: renderCode,
    link: renderLink,
    image: renderImage,
    html: renderHtml,
    listitem: renderListItem,
    table: renderTable,
    tablerow: renderTableRow,
    tablecell: renderTableCell,
  };
  return renderer;
};

const lenientHeading: TokenizerObject['heading'] = function (src) {
  const match = /^ {0,3}(#{1,6})(?!#)([^\n]*?)(?:\n+|$)/.exec(src);
  if (!match) return undefined;
  const [raw, hashes, rest] = match;
  const text = rest
    .trim()
    .replace(/\s+#+$/, '')
    .trim();
  return {
    type: 'heading',
    raw,
    depth: hashes.length,
    text,
    tokens: this.lexer.inline(text),
  };
};

const buildTokenizer = (flavor: MarkdownFlavor): TokenizerObject => {
  const tokenizer: TokenizerObject = {
    heading: lenientHeading,
    // marked has no `html: false`; suppressing the block-HTML tokenizer routes
    // raw HTML into a paragraph where the inline html renderer escapes it.
    html() {
      return undefined;
    },
  };
  if (flavor === 'original') {
    // Disable bare-URL autolinking; gfm tables and strikethrough use separate
    // tokenizers and are unaffected.
    tokenizer.url = () => undefined;
    tokenizer.autolink = () => undefined;
  }
  return tokenizer;
};

const baseOptions = { gfm: true, breaks: false, pedantic: false };

const buildConverter = (flavor: MarkdownFlavor): Marked => {
  const instance = new Marked(baseOptions);
  const reParseCell = (markdown: string): string =>
    instance.parse(markdown, { async: false });
  const extension: MarkedExtension = {
    renderer: buildRenderer(reParseCell),
    tokenizer: buildTokenizer(flavor),
    extensions: [imageWithDimensions],
  };
  instance.use(extension);
  return instance;
};

export { buildConverter, postProcessHtml };
